import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import axios from 'axios';

const API_BASE_URL = '/ap/api';

// Types
interface User {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string;  // Make role optional
  [key: string]: any;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  role: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  register: (data: any) => Promise<boolean>;
  logout: () => void;
  error: string | null;
}

// Create context
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  role: null,
  login: async () => false,
  register: async () => false,
  logout: () => {},
  error: null,
});

// Hook to use auth context
export const useAuth = () => useContext(AuthContext);

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          // Get user profile
          const response = await api.get('/auth/profile');
          const userData = response.data;
          
          // Ensure we set both user and role
          setUser(userData);
          // Use type assertion to ensure role is a string or null
          setRole(typeof userData.role === 'string' ? userData.role : null);
          setIsAuthenticated(true);
          
          // Debug log
          console.log('Auth check - User:', userData, 'Role:', userData.role);
        } catch (err) {
          // Token might be expired or invalid
          console.error('Auth check failed:', err);
          localStorage.removeItem('token');
          setUser(null);
          setRole(null);
          setIsAuthenticated(false);
        }
      }
      
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);

  // Login function
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,  // Changed from identifier to username
        password
      });

      // Ensure we set both user and role with proper type checking
      const { user, access_token } = response.data;
      setUser(user);
      // Use type assertion to ensure role is a string or null
      setRole(typeof user.role === 'string' ? user.role : null);
      setIsAuthenticated(true);
      setError(null);
      
      // Store the token
      localStorage.setItem('token', access_token);
      
      console.log('Login success - User:', user, 'Role:', user.role);
      return true;
    } catch (err) {
      console.error('Login failed:', err);
      setError('Login failed. Please check your credentials.');
      setIsAuthenticated(false);
      setUser(null);
      setRole(null);
      throw err;
    }
  };

  // Register function
  const register = async (data: any): Promise<boolean> => {
    setError(null);
    try {
      const response = await api.post('/auth/register', data);
      return true;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
      return false;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setRole(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        role,
        login,
        register,
        logout,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}; 
